// FindChurchesSheet — rebuilt per docs/FIX-FIND-CHURCHES.md, docs/designs/FindChurches.png.
//
// There was no reachable "Find churches" sheet in the app before this (the
// old design — serif title, coloured borders, CHURCH/EVENT tags, Browse
// events, "Request a church or event" — lived in ExploreTab.jsx's default
// export, which is never actually mounted anywhere; confirmed by grepping
// the whole src/ tree for `<ExploreTab`). This is a fresh build, reusing
// that file's still-live exports (CircleLogoOrInitials, CIRCLE_PASTELS) and
// the follow-sync mechanism already wired in BibleApp.jsx.
//
// Events are deliberately absent — no event rows, no Browse events, no
// "Request a church or event" (doc's explicit removal list).
//
// "Near me" note: churches have no stored lat/lng anywhere in the schema
// (checked), so real "n mi" distances aren't possible without adding
// geocoding — out of scope here. "Near me" falls back to the same
// sermon-count ordering as "Popular" rather than showing a fabricated
// distance.
//
// Props:
//   churches        — full church list ({ id, name, location, logo_url, network_id, ... })
//   homeChurchId
//   followingIds     — Set<churchId>, excludes homeChurchId
//   onToggleFollow(churchId)
//   onOpenChurch(church)
//   onInviteChurch() — shareChurchInvite()
//   onCreateChurch() — opens ChurchOnboarding for a leader setting up a new church
//   onClose()

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { CircleLogoOrInitials, CIRCLE_PASTELS } from "./ExploreTab";

const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const GOLD = "var(--gold)";

function pastelFor(id, pool) {
  const i = Math.max(0, pool.indexOf(id));
  return CIRCLE_PASTELS[i % CIRCLE_PASTELS.length];
}

export default function FindChurchesSheet({ churches, homeChurchId, followingIds, onToggleFollow, onOpenChurch, onInviteChurch, onCreateChurch, onClose }) {
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState(null); // church_id -> { sermon_count, network_id, network_name }
  const [families, setFamilies] = useState([]); // church_networks rows
  const [discoverChip, setDiscoverChip] = useState("popular"); // 'near' | 'families' | 'popular'
  const [discoverLimit, setDiscoverLimit] = useState(10);
  const [familyPage, setFamilyPage] = useState(null); // church_networks row | null
  const [confirmUnfollow, setConfirmUnfollow] = useState(null); // church | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("church_stats");
      if (!cancelled && !error) {
        const m = {};
        for (const row of data || []) m[row.church_id] = row;
        setStats(m);
      }
    })();
    supabase.from("church_networks").select("id, name, short_name, logo_url, website")
      .then(({ data }) => { if (!cancelled) setFamilies(data || []); });
    return () => { cancelled = true; };
  }, []);

  const statsFor = (id) => stats?.[id] || null;

  const home = useMemo(() => (churches || []).find(c => c.id === homeChurchId) || null, [churches, homeChurchId]);
  const homeFamily = useMemo(() => {
    if (!home?.network_id) return null;
    return families.find(f => f.id === home.network_id) || { id: home.network_id, name: statsFor(homeChurchId)?.network_name || "Church family" };
  }, [home, families, stats, homeChurchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const familyMembers = useMemo(() => {
    if (!home?.network_id) return [];
    return (churches || []).filter(c => c.network_id === home.network_id && c.id !== homeChurchId);
  }, [churches, home, homeChurchId]);
  const familyMemberIds = useMemo(() => new Set(familyMembers.map(c => c.id)), [familyMembers]);

  const otherFollowed = useMemo(() => {
    return (churches || []).filter(c => followingIds?.has(c.id) && c.id !== homeChurchId && !familyMemberIds.has(c.id));
  }, [churches, followingIds, homeChurchId, familyMemberIds]);

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;
  const searchResults = useMemo(() => {
    if (!searching) return [];
    return (churches || []).filter(c => {
      const fam = c.network_id ? families.find(f => f.id === c.network_id) : null;
      return (c.name || "").toLowerCase().includes(q)
        || (c.location || "").toLowerCase().includes(q)
        || (fam?.name || "").toLowerCase().includes(q);
    });
  }, [churches, families, q, searching]);

  const discoverPool = useMemo(() => {
    const excluded = new Set([homeChurchId, ...familyMemberIds, ...(followingIds || [])]);
    return (churches || []).filter(c => !excluded.has(c.id));
  }, [churches, homeChurchId, familyMemberIds, followingIds]);

  const discoverSorted = useMemo(() => {
    // "Near me" and "Popular" both fall back to sermon_count ordering — see
    // the file-level note on why real distance isn't available.
    return discoverPool.slice().sort((a, b) => (statsFor(b.id)?.sermon_count || 0) - (statsFor(a.id)?.sermon_count || 0));
  }, [discoverPool, stats]); // eslint-disable-line react-hooks/exhaustive-deps

  const discoverFamilies = useMemo(() => {
    const counts = {};
    for (const c of churches || []) if (c.network_id) counts[c.network_id] = (counts[c.network_id] || 0) + 1;
    return families.filter(f => counts[f.id] > 0).map(f => ({ ...f, count: counts[f.id] }));
  }, [families, churches]);

  function unfollowConfirmed() {
    if (confirmUnfollow) onToggleFollow?.(confirmUnfollow.id);
    setConfirmUnfollow(null);
  }

  // ── Family page (drill-in from "See all n") ──────────────────────────
  if (familyPage) {
    const members = (churches || []).filter(c => c.network_id === familyPage.id);
    return (
      <>
        <div className="overlay" onClick={() => setFamilyPage(null)}/>
        <div className="sheet">
          <div className="shandle"/>
          <div style={S.header}>
            <div style={S.title}>{familyPage.name}</div>
            <button type="button" onClick={() => setFamilyPage(null)} aria-label="Close" style={S.closeBtn}>✕</button>
          </div>
          <div className="shbody" style={{ paddingTop: 10 }}>
            {members.map(c => (
              <ChurchRow key={c.id} church={c} stats={statsFor(c.id)}
                isHome={c.id === homeChurchId} following={followingIds?.has(c.id)}
                onOpen={() => onOpenChurch?.(c)}
                onToggle={c.id === homeChurchId ? null : () => (followingIds?.has(c.id) ? setConfirmUnfollow(c) : onToggleFollow?.(c.id))}/>
            ))}
          </div>
        </div>
        {confirmUnfollow && <UnfollowConfirm church={confirmUnfollow} onCancel={() => setConfirmUnfollow(null)} onConfirm={unfollowConfirmed}/>}
      </>
    );
  }

  return (
    <>
      <div className="overlay" onClick={onClose}/>
      <div className="sheet" style={S.sheetWide}>
        <div className="shandle"/>
        <div style={S.header}>
          <div style={S.title}>Find churches</div>
          <button type="button" onClick={onClose} aria-label="Close" style={S.closeBtn}>✕</button>
        </div>
        <div className="shbody" style={{ paddingTop: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search churches or towns"
            style={S.search}/>

          {searching ? (
            <div style={{ marginTop: 16 }}>
              {searchResults.length === 0 ? (
                <div style={S.empty}>No churches match "{search}".</div>
              ) : searchResults.map(c => (
                <ChurchRow key={c.id} church={c} stats={statsFor(c.id)}
                  isHome={c.id === homeChurchId} following={followingIds?.has(c.id)}
                  onOpen={() => onOpenChurch?.(c)}
                  onToggle={c.id === homeChurchId ? null : () => (followingIds?.has(c.id) ? setConfirmUnfollow(c) : onToggleFollow?.(c.id))}/>
              ))}
            </div>
          ) : (
            <>
              {/* YOUR CHURCH */}
              <Section title="Your church">
                {home ? (
                  <button type="button" onClick={() => onOpenChurch?.(home)} style={S.homeCard}>
                    <span style={{ ...S.circle, width: 46, height: 46, borderRadius: 23, background: "var(--lavender)", color: "var(--lavender-ink)" }}>
                      <CircleLogoOrInitials url={home.logo_url} name={home.name}/>
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.homeBadge}>⌂ HOME CHURCH</div>
                      <div style={S.homeName}>{home.name}</div>
                      <div style={S.rowMeta}>{[home.location, statsFor(home.id)?.sermon_count ? `${statsFor(home.id).sermon_count} sermons` : null].filter(Boolean).join(" · ")}</div>
                    </div>
                    <span style={S.viewLink}>View ›</span>
                  </button>
                ) : (
                  <div style={S.empty}>No home church yet — search above to choose one.</div>
                )}
              </Section>

              {/* YOUR CHURCH FAMILY */}
              {homeFamily && familyMembers.length > 0 && (
                <Section title="Your church family" action={<button type="button" onClick={() => setFamilyPage(homeFamily)} style={S.sectionAction}>See all {familyMembers.length + 1} ›</button>}>
                  <div style={S.familyCard}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <span style={S.familyMark}><CircleLogoOrInitials url={homeFamily.logo_url} name={homeFamily.short_name || homeFamily.name}/></span>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.familyName}>{homeFamily.name}</div>
                        <div style={S.rowMeta}>{home.name} is part of this family · {familyMembers.length + 1} churches</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 14, overflowX: "auto" }}>
                      {familyMembers.slice(0, 5).map(c => {
                        const following = followingIds?.has(c.id);
                        return (
                          <div key={c.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flexShrink: 0, width: 54 }}>
                            <button type="button" onClick={() => onOpenChurch?.(c)} style={{ position: "relative", background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                              <span style={{ ...S.circle, width: 46, height: 46, borderRadius: 23, ...pastelFor(c.id, familyMembers.map(m => m.id)) }}>
                                <CircleLogoOrInitials url={c.logo_url} name={c.name}/>
                              </span>
                              <button type="button" aria-label={following ? `Unfollow ${c.name}` : `Follow ${c.name}`}
                                onClick={e => { e.stopPropagation(); following ? setConfirmUnfollow(c) : onToggleFollow?.(c.id); }}
                                style={{ position: "absolute", right: -2, bottom: -2, width: 20, height: 20, borderRadius: 10, border: "2px solid var(--white)", background: following ? "#1F1B16" : "#fff", color: following ? "#fff" : "var(--ink)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, cursor: "pointer", boxSizing: "content-box" }}>
                                {following ? "✓" : "+"}
                              </button>
                            </button>
                            <span style={S.familyMemberName}>{c.name}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--stone)", marginTop: 10 }}>Tap a church to see it · ✓ following · + to follow</div>
                  </div>
                </Section>
              )}

              {/* OTHER CHURCHES YOU FOLLOW */}
              {otherFollowed.length > 0 && (
                <Section title="Other churches you follow">
                  <div style={S.groupedCard}>
                    {otherFollowed.map((c, i) => (
                      <ChurchRow key={c.id} church={c} stats={statsFor(c.id)} divider={i > 0}
                        following onOpen={() => onOpenChurch?.(c)} onToggle={() => setConfirmUnfollow(c)}/>
                    ))}
                  </div>
                </Section>
              )}

              {/* DISCOVER */}
              <Section title="Discover">
                <div style={{ display: "flex", gap: 8, marginBottom: 12, overflowX: "auto" }}>
                  {[["near", "Near me"], ["families", "Church families"], ["popular", "Popular"]].map(([id, label]) => (
                    <button key={id} type="button" onClick={() => { setDiscoverChip(id); setDiscoverLimit(10); }}
                      style={{ ...S.chip, ...(discoverChip === id ? S.chipOn : null) }}>{label}</button>
                  ))}
                </div>
                {discoverChip === "families" ? (
                  discoverFamilies.length === 0 ? <div style={S.empty}>No other church families yet.</div> : (
                    <div style={S.groupedCard}>
                      {discoverFamilies.map((f, i) => (
                        <button key={f.id} type="button" onClick={() => setFamilyPage(f)} style={{ ...S.row, borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                          <span style={{ ...S.circle, width: 46, height: 46, borderRadius: 23, background: "#1F1B16", color: "#fff" }}>
                            <CircleLogoOrInitials url={f.logo_url} name={f.short_name || f.name}/>
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={S.rowName}>{f.name}</div>
                            <div style={S.rowMeta}>{f.count} church{f.count === 1 ? "" : "es"}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                ) : discoverSorted.length === 0 ? (
                  <div style={S.empty}>No more churches to discover.</div>
                ) : (
                  <>
                    <div style={S.groupedCard}>
                      {discoverSorted.slice(0, discoverLimit).map((c, i) => (
                        <ChurchRow key={c.id} church={c} stats={statsFor(c.id)} divider={i > 0}
                          following={followingIds?.has(c.id)} onOpen={() => onOpenChurch?.(c)}
                          onToggle={() => (followingIds?.has(c.id) ? setConfirmUnfollow(c) : onToggleFollow?.(c.id))}/>
                      ))}
                    </div>
                    {discoverSorted.length > discoverLimit && (
                      <button type="button" onClick={() => setDiscoverLimit(n => n + 10)} style={S.showMore}>Show more</button>
                    )}
                  </>
                )}
              </Section>

              {/* FOOTER */}
              <div style={S.footerCard}>
                <div style={S.footerTitle}>Can't find your church?</div>
                <div style={S.footerSub}>Invite them, or set it up if you're a leader.</div>
                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button type="button" onClick={onInviteChurch} style={S.inviteBtn}>Invite my church</button>
                  <button type="button" onClick={onCreateChurch} style={S.leaderBtn}>I'm a leader →</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {confirmUnfollow && <UnfollowConfirm church={confirmUnfollow} onCancel={() => setConfirmUnfollow(null)} onConfirm={unfollowConfirmed}/>}
    </>
  );
}

function Section({ title, action, children }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={S.sectionTitle}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function ChurchRow({ church, stats, isHome, following, divider, onOpen, onToggle }) {
  return (
    <button type="button" onClick={onOpen} style={{ ...S.row, borderTop: divider ? "1px solid var(--border)" : "none" }}>
      <span style={{ ...S.circle, width: 46, height: 46, borderRadius: 23, background: isHome ? "var(--lavender)" : "var(--sage)", color: isHome ? "var(--lavender-ink)" : "var(--sage-ink)" }}>
        <CircleLogoOrInitials url={church.logo_url} name={church.name}/>
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.rowName}>{church.name}</div>
        <div style={S.rowMeta}>{[church.location, stats?.sermon_count ? `${stats.sermon_count} sermons` : null].filter(Boolean).join(" · ")}</div>
      </div>
      {onToggle && (
        <span role="button" tabIndex={0}
          onClick={e => { e.stopPropagation(); onToggle(); }}
          onKeyDown={e => { if (e.key === "Enter") { e.stopPropagation(); onToggle(); } }}
          style={following ? S.followingPill : S.followBtn}>
          {following ? "✓ Following" : "Follow"}
        </span>
      )}
    </button>
  );
}

function UnfollowConfirm({ church, onCancel, onConfirm }) {
  return (
    <>
      <div className="overlay" onClick={onCancel} style={{ zIndex: 600 }}/>
      <div className="sheet" style={{ zIndex: 601, maxHeight: "none" }}>
        <div className="shandle"/>
        <div style={{ padding: "4px 20px 20px" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--ink)", marginBottom: 6 }}>Unfollow {church.name}?</div>
          <div style={{ fontSize: 13.5, color: "var(--stone)", lineHeight: 1.45, marginBottom: 16 }}>You won't see their sermons or notes unless you follow them again.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onCancel} style={{ flex: 1, background: "var(--white)", border: "1px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "11px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS }}>Cancel</button>
            <button type="button" onClick={onConfirm} style={{ flex: 1, background: "#1F1B16", border: "none", color: "#fff", borderRadius: 999, padding: "11px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: SANS }}>Unfollow</button>
          </div>
        </div>
      </div>
    </>
  );
}

const S = {
  sheetWide: { maxWidth: 560, marginLeft: "auto", marginRight: "auto" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 18px 2px" },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", fontFamily: SANS },
  closeBtn: { flexShrink: 0, width: 34, height: 34, borderRadius: 17, background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  search: { width: "100%", boxSizing: "border-box", background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 999, padding: "12px 16px", fontSize: 14.5, fontFamily: SANS, color: "var(--ink)", outline: "none" },
  sectionTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--gold-ink)" },
  sectionAction: { background: "none", border: "none", color: "var(--gold-ink)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 },
  empty: { fontSize: 13, color: "var(--stone)", padding: "14px 2px" },

  circle: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, overflow: "hidden" },

  homeCard: { width: "100%", display: "flex", alignItems: "center", gap: 14, background: "var(--white)", border: "none", borderRadius: 18, padding: "14px 16px", cursor: "pointer", textAlign: "left", fontFamily: SANS },
  homeBadge: { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--gold-ink)" },
  homeName: { fontSize: 16, fontWeight: 800, color: "var(--ink)", marginTop: 1 },
  viewLink: { flexShrink: 0, fontSize: 13, fontWeight: 800, color: "var(--gold-ink)" },

  familyCard: { background: "var(--white)", borderRadius: 18, padding: 16 },
  familyMark: { flexShrink: 0, width: 38, height: 38, borderRadius: 10, background: "#1F1B16", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, overflow: "hidden" },
  familyName: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  familyMemberName: { fontSize: 11, fontWeight: 700, color: "var(--stone)", textAlign: "center" },

  groupedCard: { background: "var(--white)", borderRadius: 18, overflow: "hidden" },
  row: { display: "flex", alignItems: "center", gap: 14, width: "100%", background: "none", border: "none", padding: "13px 16px", cursor: "pointer", textAlign: "left", fontFamily: SANS },
  rowName: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  rowMeta: { fontSize: 12.5, color: "var(--stone)", marginTop: 2 },

  followBtn: { flexShrink: 0, background: "#1F1B16", color: "#fff", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 800, fontFamily: SANS, cursor: "pointer" },
  followingPill: { flexShrink: 0, display: "flex", alignItems: "center", gap: 4, background: "var(--cream)", color: "var(--ink)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, fontFamily: SANS, cursor: "pointer" },

  chip: { flexShrink: 0, background: "var(--white)", border: "none", borderRadius: 999, padding: "9px 15px", fontSize: 13, fontWeight: 700, color: "var(--stone)", cursor: "pointer", fontFamily: SANS, whiteSpace: "nowrap" },
  chipOn: { background: "#1F1B16", color: "#fff" },
  showMore: { display: "block", width: "100%", marginTop: 10, background: "var(--white)", border: "1px solid var(--border-strong)", color: "var(--gold-ink)", borderRadius: 10, padding: "11px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: SANS },

  footerCard: { marginTop: 24, background: "var(--cream)", border: "1px solid var(--border)", borderRadius: 18, padding: 18, textAlign: "center" },
  footerTitle: { fontSize: 15, fontWeight: 800, color: "var(--ink)" },
  footerSub: { fontSize: 12.5, color: "var(--stone)", marginTop: 3 },
  inviteBtn: { flex: 1, background: "var(--white)", border: "1.5px solid var(--border-strong)", color: "var(--ink)", borderRadius: 999, padding: "11px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
  leaderBtn: { flex: 1, background: GOLD, border: "none", color: "var(--ink)", borderRadius: 999, padding: "11px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: SANS },
};
